import { Component, HostListener, Input, OnInit, AfterViewInit, ChangeDetectorRef, Inject } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { PackageSummaryComponent } from '@oneqrew-partnertool/package-summary';
import { PageGuard, PlanCalculatorService, PLANS_HELPER, SharedModule } from '@oneqrew-partnertool/utils';
import { debounceTime, take } from 'rxjs';
import { NewCustomerContactComponent } from '@oneqrew-partnertool/new-customer-contact';

import {
  selectCustomer,
  selectFetchedPlans,
  selectFetchedSalesPartners,
  selectPlan,
  setCustomer
} from '../../../../../../landingpage/core/src';
import {
  CustomerOneqrewSubscription,
  CustomerSalesPartner,
  CustomerService,
  OneqrewModule,
  OneqrewProductPackage
} from '../../../../../data-access/api-client/src';
import { Customer, Module, Plan } from '../../../../../../landingpage/models/src';
import { CustomerOffer } from '../../../../../data-access/api-client/src/lib/model/customerOffer';

@Component({
  selector: 'package-summary-dialog',
  standalone: true,
  imports: [PackageSummaryComponent, NewCustomerContactComponent, SharedModule],
  templateUrl: './package-summary-dialog.component.html',
  styleUrls: ['./package-summary-dialog.component.scss']
})
export class PackageSummaryDialogComponent implements OnInit, AfterViewInit {
  class = 'package-summary-dialog';
  offerForm!: FormGroup;
  userID: string = '';
  customer!: Customer;
  @Input() isNewPlanPage = false;
  offerFormValid!: boolean;
  canRenderSummary = false;
  plan!: Plan;
  sendingOffer: boolean = false;
  sendOfferResult?: string = undefined;
  plans: Plan[] = [];

  constructor(
    private store: Store,
    private fb: FormBuilder,
    private pageGuard: PageGuard,
    private cdr: ChangeDetectorRef,
    private customerService: CustomerService,
    private planCalculatorService: PlanCalculatorService
  ) {}

  ngOnInit(): void {
    this.offerForm = this.fb.group({});

    this.offerForm.statusChanges.pipe(debounceTime(500)).subscribe((status) => {
      this.offerFormValid = status == 'VALID';
    });

    this.store.select(selectCustomer).subscribe((customer) => {
      this.customer = customer;
    });

    this.store.select(selectPlan).subscribe((plan) => {
      this.plan = plan;
    });

    //TODO: Rewrite extra modules included in the calculation handling
    this.store.select(selectFetchedPlans).subscribe((plans) => {
      this.plans = plans;
    });
  }

  ngAfterViewInit(): void {
    this.canRenderSummary = true;
    this.cdr.detectChanges();
  }

  onFormSubmit() {
    if (this.offerForm.valid) {
      let salesPartner;
      this.store
        .select(selectFetchedSalesPartners)
        .pipe(take(1))
        .subscribe((state) => {
          salesPartner = state.find((salesPartner) => salesPartner.id == this.offerForm.value.salesPartner);
        });

      const updatedCustomer = {
        ...this.customer,
        firstName: this.offerForm.value.firstName,
        lastName: this.offerForm.value.lastName,
        email: this.offerForm.value.email,
        company: this.offerForm.value.company
      };

      this.pageGuard.setNavigatedFromValidUrl(true);
      this.store.dispatch(setCustomer(updatedCustomer));

      const filterModules = this.getFilteredAdditionalModules(this.plan); // Not include default additional modules.
      const mappedModules: OneqrewModule[] =
        filterModules?.map((module) => ({
          id: module.id,
          name: module.name,
          monthlyPrice: module.price,
          isExtra: true
        })) || [];

      const mappedPackage: OneqrewProductPackage = {
        name: PLANS_HELPER.getName(this.plan.packageType),
        monthlyPrice: this.plan.price,
        type: this.plan.packageType
      };
      const legacyProductDiscount = this.offerForm.value.legacyProductDiscount;
      const oneqrewsubscription: CustomerOneqrewSubscription = {
        monthlyCostSum: this.planCalculatorService.getNewPlanTotal(this.plan, legacyProductDiscount?.discount ?? 0) || 0,
        endOfContract: Plan.formatDateAsString(Plan.getEndOfContract(this.plan, true)),
        startOfContract: Plan.formatDateAsString(Plan.getStartOfContract(this.plan, true)),
        amountDevices: this.plan.devicesCount,
        amountWorkspaces: this.plan.workStationCount,
        amountClients: this.plan.clientsCount,
        isCloudVersion: this.plan.cloudVersion,
        extraModules: mappedModules,
        productPackage: mappedPackage,
        legacyProductDiscount: legacyProductDiscount,
        comment: this.offerForm.value.comment
      };

      const customerOffer: CustomerOffer = {
        firstName: updatedCustomer.firstName,
        lastName: updatedCustomer.lastName,
        companyName: updatedCustomer.company,
        email: updatedCustomer.email,
        salesPartnerEmail: this.offerForm.value.salesPartnerEmail,
        salesPartner: { id: this.offerForm.value.salesPartner } as CustomerSalesPartner,
        oneqrewSubscription: oneqrewsubscription,
        ordered: false,
        creationDate: this.formatDate(new Date())
      };
      this.sendingOffer = true;
      this.customerService.createCustomerOffer(customerOffer).subscribe({
        next: (response) => {
          console.log('Form submitted successfully. Response:', response);
        },
        error: (error) => {
          this.sendingOffer = false;
          this.sendOfferResult =
            'Beim Senden Ihrer Anfrage ist ein Fehler aufgetreten. Bitte versuchen Sie es zu einem späteren Zeitpunkt erneut.';
          console.error('Error occurred while submitting form. Details:', error);
        },
        complete: () => {
          this.sendingOffer = false;
          this.sendOfferResult = 'Das Angebot wurde an die E-Mail Adresse Ihres Kunden gesendet.';
          this.offerForm.reset();
        }
      });
    } else {
      this.offerForm.markAllAsTouched();
      console.error('Form is invalid');
    }
  }

  formInitialized(form: FormGroup) {
    for (const proprty in form.controls) {
      this.offerForm.setControl(proprty, form.controls[proprty]);
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    $event.returnValue = 'Beim erneuten Laden der Seite gehen die ausgewählten Module und der Plan verloren.';
  }

  private formatDate(date: Date): string {
    const startDate = new Date(date);
    const startDateYear = startDate.getFullYear();
    const startDateMonth = String(startDate.getMonth() + 1).padStart(2, '0'); // Next Month for new customers/order app
    return `${startDateYear}-${startDateMonth}-01T00:00:00Z`;
  }

  private getFilteredAdditionalModules(plan: Plan): Module[] {
    const defaultAdditionalModuleIds = this.plans.find((p) => p.planType == plan.planType)?.additionalModules?.map((m) => m.id) || [];
    return plan.additionalModules?.filter((m) => !defaultAdditionalModuleIds.includes(m.id)) || [];
  }
}
